/**
 * 封装axios 用于 网关接口请求
 */
import axios from 'axios';
import store from '@/store';
import config from '@/config';
import router from '@/router';
import gateway from '@/request/gateway';
import { Message } from 'element-ui';
import { TOKEN_TIMEOUT } from '@/config/constant';
import { getI18nText } from '../i18n';

const addErrorLog = error => {
	const {
		statusText,
		status,
		request: { responseURL }
	} = error;

	let info = {
		type: 'api',
		status,
		message: statusText,
		url: responseURL
	};
	store.dispatch('addErrorLog', info);
};

/*是否有请求正在刷新token*/
window.isRefreshing = false;
/* 被挂起的请求数组 */
let refreshSubscribers = [];
/**
 * 判断token是否快要过期
 * @returns boolean
 */
function isExpires() {
	if (localStorage.getItem('expires_time')) {
		let expires = localStorage.getItem('expires_time');
		if (expires - +new Date() < TOKEN_TIMEOUT) {
			return true;
		}
		return false;
	} else {
		return false;
	}
}
/**
 * push所有请求到数组中 （refreshSubscribers数组中的请求得到新的token之后会自执行，用新的token去请求数据）
 * @param { function } callback
 */
function subscribeTokenRefresh(callback) {
	refreshSubscribers.push(callback);
}
/**
 * 刷新请求
 */
function onRefreshed(token) {
	refreshSubscribers.map(cb => cb(token));
}

// 设置超时
const TIMEOUT = 30;

// 创建axios实例
const instance = axios.create({
	headers: config.headers,
	timeout: TIMEOUT * 1000 // request timeout (s)
});

/**
 * 添加请求拦截
 */
instance.interceptors.request.use(
	request => {
		/**
		 * 添加网关验证的请求头
		 */
		let headers = gateway(request);
		request.headers = Object.assign(request.headers, {
			'x-ca-key': headers.key,
			'x-ca-signature': headers.signature,
			'x-ca-signature-Headers': headers.signatureHeaders,
			// 'x-ca-timestamp': headers.timestamp,
			'x-ca-nonce': headers.nonce
		});
		request.headers['xblang'] = localStorage.getItem('lang');
		console.log('headers', request.headers);
		/**
		 * 请求携带token
		 */
		if (localStorage.token) {
			request.headers['Authorization'] = localStorage.token;
		}
		/**
		 * 判断token是否即将过期且不是刷新token接口
		 */
		if (isExpires() && request.url.indexOf('refreshToken') === -1) {
			/**
			 * 判断是否正在刷新
			 */
			if (!window.isRefreshing) {
				window.isRefreshing = true;
				store
					.dispatch('refreshToken')
					.then(() => {
						let token = localStorage.token;
						/**
						 * 重新设置请求头 携带新的token
						 */
						request.headers['Authorization'] = token;
						/**
						 * 执行数组里的请求，重新发起被挂起的请求
						 */
						onRefreshed(token);
					})
					.catch(err => {
						console.error(err);
						store.dispatch('logout').then(() => {
							router.replace({
								name: 'login',
								query: {
									redirect: router.currentRoute.name,
									type: 'refresh'
								}
							});
						});
					});
				/**
				 * 挂起请求
				 */
				let retry = new Promise(resolve => {
					subscribeTokenRefresh(token => {
						request.headers['Authorization'] = token;
						resolve(request);
					});
				});

				return retry;
			}
		} else {
			return request;
		}
	},
	err => {
		return Promise.reject(err);
	}
);

/**
 * 添加响应拦截
 */
instance.interceptors.response.use(
	res => {
		if (res && res.data) {
			return Promise.resolve(res.data);
		} else {
			Message({
				message: getI18nText('请求错误'),
				type: 'error',
				duration: 5 * 1000
			});
			return Promise.reject(res);
		}
	},
	err => {
		// 如果http状态码：401 说明未授权， 登录已过期， 直接跳转到登录页
		console.error('err response', err);
		let code = err.response.headers['x-ca-error-code'];
		// 新增：判断是否是 500 错误
		if (err.response && err.response.status === 500) {
			const message =
				(err.response.data && err.response.data.message) ||
				getI18nText('服务器内部错误，请联系管理员');
			Message({
				message,
				type: 'error',
				duration: 5 * 1000
			});
		} else if (code === 'A403JE') {
			Message({
				message: getI18nText('登录已过期， 请重新登陆'),
				duration: 2 * 1000
			});
			store.dispatch('logout').then(() => {
				router.replace({
					name: 'login',
					query: {
						redirect: router.currentRoute.name,
						type: 'expires'
					}
				});
			});
			// 其他情况， 默认请求失败
		} else {
			Message({
				message: getI18nText('请求失败,请刷新重试'),
				type: 'error',
				duration: 5 * 1000
			});

			if (config.log) {
				// 获取错误信息
				let error = err.response;
				if (!error) {
					const {
						request: { statusText, status },
						config
					} = JSON.parse(JSON.stringify(err));

					error = {
						statusText,
						status,
						request: { responseURL: config.url }
					};
				}
				// 上报api异常信息
				addErrorLog(error);
			}
		}
		return Promise.reject(err);
	}
);

export default instance;
